'use client';

import { formatCurrency } from '@ahm/common-helpers';
import { useMyProfile } from '@ahm/react-auth';
import {
  ActionIcon,
  Box,
  Button,
  CopyButton,
  Stack,
  Text,
  TextInput,
  Tooltip,
  rem,
} from '@mantine/core';
import { Check, Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { createVirtualAccount } from '@/api/payment/create-virtual-account-fn';

interface VPBankPrepaidTopupProps {
  amount: number;
  onComplete?: () => void;
  onBack?: () => void;
  onClose?: () => void;
}

export default function VPBankPrepaidTopup({
  amount,
  onComplete,
  // onBack,
  // onClose,
}: VPBankPrepaidTopupProps) {
  const { user, refetch } = useMyProfile();

  const virtualAccount = useMemo(
    () => user?.wallet_info?.bank?.virtual_account,
    [user?.wallet_info?.bank?.virtual_account]
  );

  const initData = useCallback(async () => {
    if (!virtualAccount) {
      let res;
      try {
        res = await createVirtualAccount({
          name: 'PREPAID',
        });
      } catch (err) {
        console.log('err', err);
      }

      if (res && res.status === 'success' && refetch) {
        void refetch();
      }
    }
  }, [refetch, virtualAccount]);

  useEffect(() => {
    void initData();
  }, [initData]);

  // Helper component để tránh duplicate code
  const CopyableTextInput = ({
    label,
    value,
    copyValue,
  }: {
    label: string;
    value: string;
    copyValue?: string;
  }) => (
    <Box>
      <Text size="sm" c="dimmed" mb="xs">
        {label}
      </Text>
      {copyValue ? (
        <CopyButton value={copyValue} timeout={2000}>
          {({ copied, copy }) => (
            <TextInput
              value={value}
              readOnly
              styles={{
                input: {
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  fontWeight: 600,
                  ...(label === 'Số tiền' && { fontSize: '16px' }),
                },
              }}
              rightSection={
                <Tooltip
                  label={copied ? 'Đã sao chép' : 'Sao chép'}
                  withArrow
                  position="left"
                >
                  <ActionIcon
                    color={copied ? 'green.6' : 'primary.6'}
                    variant="transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      copy();
                    }}
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </ActionIcon>
                </Tooltip>
              }
            />
          )}
        </CopyButton>
      ) : (
        <TextInput
          value={value}
          readOnly
          styles={{
            input: {
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              fontWeight: 600,
            },
          }}
        />
      )}
    </Box>
  );

  return (
    <>
      <Box px={rem(24)}>
        <Stack gap="lg" pb={rem(90)}>
          <Text size="sm" c="dimmed" ta="center">
            Vui lòng chuyển số tiền cần nạp theo thông tin chuyển khoản dưới
            đây. Tiền nạp sẽ tự động cộng vào tài khoản chính sau khi bạn chuyển
            khoản thành công.
          </Text>

          <Stack gap="md">
            <CopyableTextInput
              label="Số tiền"
              value={formatCurrency(amount)}
              copyValue={amount.toString()}
            />

            <CopyableTextInput
              label="Tên ngân hàng"
              value="NH TMCP VIET NAM THINH VUONG VPBANK"
            />

            <CopyableTextInput label="Chi nhánh" value="CHI NHANH TPHCM" />

            <CopyableTextInput
              label="Số tài khoản"
              value={virtualAccount?.id ?? ''}
              copyValue={virtualAccount?.id ?? ''}
            />

            <CopyableTextInput
              label="Người thụ hưởng"
              value={virtualAccount?.name ?? ''}
              copyValue={virtualAccount?.name ?? ''}
            />

            <CopyableTextInput
              label="Nội dung chuyển khoản"
              value="NAP TIEN TAI KHOAN CHINH"
              copyValue="NAP TIEN TAI KHOAN CHINH"
            />
          </Stack>
        </Stack>

        <Box
          pos="fixed"
          bottom={0}
          left={0}
          right={0}
          bg="white"
          p={rem(16)}
          style={{
            zIndex: 10,
            borderTop: '1px solid #e9ecef',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Button
            fullWidth
            h={rem(48)}
            fw={700}
            bg="orange"
            c="white"
            onClick={onComplete}
            styles={{
              root: {
                '&:hover': {
                  backgroundColor: '#e67e22',
                },
              },
            }}
          >
            HOÀN TẤT
          </Button>
        </Box>
      </Box>
    </>
  );
}
